import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import {
  DEFAULT_MODULE_TYPE,
  DOCUMENT_TYPE_PDF_ONLY,
  VIEW_PROFILE_SECTIONS,
} from "Constant/constants";
import { Validator } from "Utils/validator";
import { uploadHandler } from "Utils";

const RecognitionTypes = ["Award", "Recognition", "Activity", "Other"];

export const RecognitionModal = ({
  show,
  recognitionData,
  toggle,
  fetchData,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    gid: "",
    typeOfRecognition: "",
    otherTypeOfRecognition: "",
    titleOfTheAwardOrRecognition: "",
    awardedOrganization: "",
    dateOfActivity: "",
    awardDescription: "",
    linkToFurtherDetails: "",
    uploadCertificateOrDocument: null,
  });
  const [errMsg, setErrMsg] = useState({});

  const {
    gid,
    typeOfRecognition,
    otherTypeOfRecognition,
    titleOfTheAwardOrRecognition,
    awardedOrganization,
    dateOfActivity,
    awardDescription,
    linkToFurtherDetails,
    uploadCertificateOrDocument,
  } = formData;

  const validationRequiredFields = [
    "typeOfRecognition",
    "titleOfTheAwardOrRecognition",
    "awardedOrganization",
    "dateOfActivity",
    "awardDescription",
  ];

  useEffect(() => {
    if (recognitionData) {
      setFormData({
        gid: recognitionData.gid || "",
        titleOfTheAwardOrRecognition: recognitionData.title || "",
        typeOfRecognition: recognitionData.type || "",
        otherTypeOfRecognition: recognitionData.otherTypeOfRecognition || "",
        awardedOrganization: recognitionData.awardedOrganization || "",
        dateOfActivity: recognitionData.dateOfActivity || "",
        awardDescription: recognitionData.awardDescription || "",
        linkToFurtherDetails: recognitionData.linkToFurtherDetails || "",
        uploadCertificateOrDocument:
          recognitionData.uploadCertificateOrDocument || null,
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));

    if (validationRequiredFields.includes(name)) {
      if (!Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(
            `common.messages.error.viewProfile.recognition.${name}.blank`
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handleFileChange = async (e) => {
    const response = await uploadHandler(
      e,
      DEFAULT_MODULE_TYPE.userDocuments,
      DOCUMENT_TYPE_PDF_ONLY
    );
    if (response) {
      setFormData((prev) => ({
        ...prev,
        uploadCertificateOrDocument: response,
      }));
    }
  };

  const handleFileRemove = () => {
    setFormData((prev) => ({
      ...prev,
      uploadCertificateOrDocument: null,
    }));
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      // Validate always-required fields
      for (const field of validationRequiredFields) {
        const value = formData[field];
        if (!Validator.text(value)) {
          errors[field] = t(
            `common.messages.error.viewProfile.recognition.${field}.blank`
          );
          isValid = false;
        }
      }

      // Set error messages
      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSaveRecognition = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.recognition,
          [VIEW_PROFILE_SECTIONS.recognition]: {
            title: titleOfTheAwardOrRecognition,
            type: typeOfRecognition,
            otherTypeOfRecognition,
            awardedOrganization,
            dateOfActivity,
            awardDescription,
            linkToFurtherDetails,
            uploadCertificateOrDocument,
          },
        };

        if (gid.trim()) {
          payload[VIEW_PROFILE_SECTIONS.recognition].gid = gid;
        }

        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal size="lg" show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t(`auth.viewProfile.${gid ? "editRecognition" : "addRecognition"}`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.titleOfTheAwardOrRecognition")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="titleOfTheAwardOrRecognition"
              placeholder={t("common.placeHolder.titleOfTheAwardOrRecognition")}
              value={formData.titleOfTheAwardOrRecognition}
              onChange={handleChange}
            />
            {errMsg.titleOfTheAwardOrRecognition && (
              <p className="error">{errMsg.titleOfTheAwardOrRecognition}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.typeOfRecognition")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              name="typeOfRecognition"
              value={formData.typeOfRecognition}
              onChange={handleChange}
            >
              <option value="">
                {t("common.placeHolder.typeOfRecognition")}
              </option>
              {RecognitionTypes.map((type) => (
                <option value={type} key={type}>
                  {type}
                </option>
              ))}
            </select>
            {formData.typeOfRecognition === "Other" && (
              <div className="my-3">
                <input
                  type="text"
                  className="form-control mt-2"
                  name="otherTypeOfRecognition"
                  placeholder={t("common.placeHolder.otherTypeOfRecognition")}
                  value={formData.otherTypeOfRecognition}
                  onChange={handleChange}
                />
              </div>
            )}
            {errMsg.typeOfRecognition && (
              <p className="error">{errMsg.typeOfRecognition}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.awardedOrganization")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="awardedOrganization"
              placeholder={t("common.placeHolder.awardedOrganization")}
              value={formData.awardedOrganization}
              onChange={handleChange}
            />
            {errMsg.awardedOrganization && (
              <p className="error">{errMsg.awardedOrganization}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.dateOfActivity")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="dateOfActivity"
              placeholder={t("common.placeHolder.dateOfActivity")}
              value={formData.dateOfActivity}
              onChange={handleChange}
            />
            {errMsg.dateOfActivity && (
              <p className="error">{errMsg.dateOfActivity}</p>
            )}
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">
              {t("common.label.awardDescription")}{" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              name="awardDescription"
              placeholder={t("common.placeHolder.awardDescription")}
              value={formData.awardDescription}
              onChange={handleChange}
              rows={4}
            />
            {errMsg.awardDescription && (
              <p className="error">{errMsg.awardDescription}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.linkToFurtherDetails")}
            </label>
            <input
              type="text"
              className="form-control"
              name="linkToFurtherDetails"
              placeholder={t("common.placeHolder.linkToFurtherDetails")}
              value={formData.linkToFurtherDetails}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.uploadCertificateOrDocument")}
            </label>
            {uploadCertificateOrDocument ? (
              <div className="">
                <div className="form-control py-0-5 pr-0-5 d-flex align-items-center justify-content-between">
                  <span className="">
                    {uploadCertificateOrDocument.name} (
                    {uploadCertificateOrDocument.size})
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger-light ms-2 w-7 h-7 p-0 d-flex justify-content-center align-items-center"
                    onClick={handleFileRemove}
                  >
                    <i className="bx bx-trash"></i>
                  </button>
                </div>
              </div>
            ) : (
              <input
                type="file"
                className="form-control"
                name="uploadCertificateOrDocument"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
              />
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button variant="primary" onClick={onSaveRecognition}>
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
